/** HLE model-testing core (docs/hle-testing-engine-design.md). Pure + dependency-free. */

export const HLE_MODELS = ['gpt', 'gemini', 'opus', 'kimi', 'musespark'] as const;
export type HleModelId = (typeof HLE_MODELS)[number];

/** model id → delivery output column header (matched by NAME, never A/B/C position). */
export const HLE_OUTPUT_COLUMN: Record<HleModelId, string> = {
  gpt: 'GPT_MODEL_OUTPUT',
  gemini: 'GEMINI_MODEL_OUTPUT',
  opus: 'OPUS_MODEL_OUTPUT',
  kimi: 'KIMI_MODEL_OUTPUT',
  musespark: 'MUSESPARK_MODEL_OUTPUT',
};

/** Input columns the engine reads (normalized UPPER_SNAKE header names). */
export const HLE_COLUMNS = {
  readyForTest: 'READY_FOR_TEST',
  languageCode: 'LANGUAGE_CODE',
  itemId: 'ITEM_ID',
  answerType: 'ANSWER_TYPE',
  question: 'QUESTION',
} as const;

export type HleAnswerVariant = 'short' | 'long';

const SHORT_ANSWER_TYPES = new Set([
  'exactmatch', 'exact', 'multiplechoice', 'multiplechoicequestion', 'mcq',
]);
const LONG_ANSWER_TYPES = new Set([
  'constructedresponse', 'constructed', 'longform', 'longformat', 'longanswer', 'long',
]);

/**
 * ANSWER_TYPE → prompt variant. The template's data validation (HLE Template.xlsx,
 * "Format checklist") locks the column to `ExactMatch` or `MultipleChoice` — camelCase,
 * no separators — so matching strips ALL separators rather than collapsing them to
 * spaces; that way `MultipleChoice`, `Multiple Choice`, `multiple_choice` and
 * `Exact-Match` all resolve. Long-form (`ConstructedResponse`) is accepted too: it is
 * not in this template's dropdown but is the guidelines' second answer type (§3.3).
 */
export function answerVariant(answerType: string): HleAnswerVariant | null {
  const s = answerType.trim().toLowerCase().replace(/[\s_.\-]+/g, '');
  if (SHORT_ANSWER_TYPES.has(s)) return 'short';
  if (LONG_ANSWER_TYPES.has(s)) return 'long';
  return null;
}

const SHORT_PROMPT = (lang: string) =>
  `You are a linguistic and cultural consultant who needs to answer a difficult question for a client. Your task is to provide your answer in ${lang}. Provide the exact answer or the letter from a multiple choice question. You are not a teacher, do not lecture about the topic of the question but just answer the question. Do not copy the question in your response. Do not restate the question in paraphrased form in your response. Do not use introductory language (such as "Here is your answer" or "The answer is"). Do not apologize, and strictly follow the output format if specified.`;
const LONG_PROMPT = (lang: string) =>
  `You are a linguistic and cultural consultant who needs to answer a difficult question for a client. Your task is to provide your answer in ${lang}. Provide an answer between 500 and 600 words. You are not a teacher, do not lecture about the topic of the question but just answer the question. Do not copy the question in your response. Do not restate the question in paraphrased form in your response. Do not use introductory language (such as "Here is your answer" or "The answer is"). Do not apologize, and strictly follow the output format if specified.`;

export function systemPromptFor(variant: HleAnswerVariant, targetLanguage: string): string {
  return variant === 'long' ? LONG_PROMPT(targetLanguage) : SHORT_PROMPT(targetLanguage);
}

/** code → exact §2.4 English name (26 languages). */
export const HLE_LANGUAGES: Record<string, string> = {
  spa_Latn: 'Spanish', por_Latn: 'Portuguese', hin_Deva: 'Hindi', ind_Latn: 'Indonesian',
  arb_Arab: 'Standard Arabic', tur_Latn: 'Turkish', ben_Beng: 'Bengali', fra_Latn: 'French',
  deu_Latn: 'German', jpn_Jpan: 'Japanese', mar_Deva: 'Marathi', guj_Gujr: 'Gujarati',
  kan_Knda: 'Kannada', tel_Telu: 'Telugu', fil_Latn: 'Filipino', vie_Latn: 'Vietnamese',
  rus_Cyrl: 'Standard Russian', tha_Thai: 'Thai', urd_Arab: 'Urdu', jav_Latn: 'Javanese',
  ita_Latn: 'Italian', pol_Latn: 'Polish', tam_Taml: 'Tamil', mal_Mlym: 'Malayalam',
  cmn_Hans: 'Mandarin Chinese (Simplified)', kor_Kore: 'Korean',
};

export function languageName(code: string): string | null {
  return HLE_LANGUAGES[code.trim()] ?? null;
}

/** Script segment of `iso_Script` → max output tokens (Latn 16K, else 32K). */
export function maxTokensFor(languageCode: string): number {
  const script = languageCode.trim().split('_')[1] ?? '';
  return script === 'Latn' ? 16000 : 32000;
}

export function isReadyForTest(cell: string): boolean {
  return ['true', 'yes', 'y', '1', '✓', '✔', 'x'].includes(cell.trim().toLowerCase());
}

export interface HleItem {
  rowNumber: number;
  itemId: string;
  languageCode: string;
  targetLanguage: string;
  answerType: string;
  variant: HleAnswerVariant;
  question: string;
  maxTokens: number;
}

export type HleRowParse =
  | { ok: true; item: HleItem }
  | { ok: false; skip: 'not-ready' }
  | { ok: false; error: string };

/** Parse one positioned row (via a header→value getter) into a runnable item, or a skip/error. */
export function parseHleRow(rowNumber: number, get: (col: string) => string): HleRowParse {
  if (!isReadyForTest(get(HLE_COLUMNS.readyForTest))) return { ok: false, skip: 'not-ready' };
  const languageCode = get(HLE_COLUMNS.languageCode).trim();
  const targetLanguage = languageName(languageCode);
  if (!targetLanguage) return { ok: false, error: `unknown LANGUAGE_CODE "${languageCode || '(empty)'}"` };
  const answerType = get(HLE_COLUMNS.answerType).trim();
  const variant = answerVariant(answerType);
  if (!variant) return { ok: false, error: `unknown ANSWER_TYPE "${answerType || '(empty)'}"` };
  const question = get(HLE_COLUMNS.question).trim();
  if (!question) return { ok: false, error: 'QUESTION is empty' };
  return {
    ok: true,
    item: { rowNumber, itemId: get(HLE_COLUMNS.itemId).trim(), languageCode, targetLanguage, answerType, variant, question, maxTokens: maxTokensFor(languageCode) },
  };
}

// --- sheet header validation ------------------------------------------------

export interface HleHeaderCheck {
  /** Required INPUT headers absent from the sheet (engine cannot run without these). */
  missingInput: string[];
  /** Model-output headers (O–S) absent from the sheet — those models can't be written. */
  missingOutput: HleModelId[];
}

/**
 * Validate a sheet's normalized header row before a run.
 *
 * This exists because a missing header is otherwise INVISIBLE: `parseHleRow` reads an
 * absent column as `''`, so a sheet without `READY_FOR_TEST` (the engine's own control
 * column — it is NOT in the delivery template and has to be added by hand) yields "0
 * rows ready" and a clean, successful, completely useless run. Callers must treat a
 * non-empty `missingInput` as a hard error rather than a zero.
 */
export function checkHleHeaders(normalizedHeaders: Iterable<string>): HleHeaderCheck {
  const present = new Set(normalizedHeaders);
  return {
    missingInput: Object.values(HLE_COLUMNS).filter((c) => !present.has(c)),
    missingOutput: HLE_MODELS.filter((m) => !present.has(HLE_OUTPUT_COLUMN[m])),
  };
}

// --- token + cost accounting ------------------------------------------------

export interface HleModelUsage {
  /** Successful calls that returned an answer. */
  calls: number;
  tokensIn: number;
  tokensOut: number;
}

export function emptyUsage(): Record<HleModelId, HleModelUsage> {
  return Object.fromEntries(
    HLE_MODELS.map((m) => [m, { calls: 0, tokensIn: 0, tokensOut: 0 }]),
  ) as Record<HleModelId, HleModelUsage>;
}

/** USD per 1M tokens. `null` = not published/verified here; supply it via env (see `hlePricingFromEnv`). */
export interface HleModelPrice { in: number; out: number }

/**
 * Default prices. Only Anthropic's first-party rate is filled in because it is the one
 * we can state with confidence; the other four are operator-supplied so the dashboard
 * never shows an invented number. Missing prices make the estimate PARTIAL, not wrong.
 */
export const HLE_DEFAULT_PRICING: Partial<Record<HleModelId, HleModelPrice>> = {
  opus: { in: 5, out: 25 },
};

/**
 * Read `HLE_PRICE_<MODEL>_IN` / `_OUT` (USD per 1M tokens, e.g. `HLE_PRICE_GPT_IN=1.25`)
 * over the defaults, so an operator can price every model without a code change.
 */
export function hlePricingFromEnv(env: Record<string, string | undefined>): Partial<Record<HleModelId, HleModelPrice>> {
  const out: Partial<Record<HleModelId, HleModelPrice>> = { ...HLE_DEFAULT_PRICING };
  for (const m of HLE_MODELS) {
    const i = Number(env[`HLE_PRICE_${m.toUpperCase()}_IN`]);
    const o = Number(env[`HLE_PRICE_${m.toUpperCase()}_OUT`]);
    if (Number.isFinite(i) && Number.isFinite(o) && i >= 0 && o >= 0) out[m] = { in: i, out: o };
  }
  return out;
}

export interface HleCostEstimate {
  /** USD across every model we have a price for. */
  usd: number;
  /** Models that ran but have no configured price — the estimate excludes them. */
  unpriced: HleModelId[];
}

export function estimateCost(
  usage: Record<HleModelId, HleModelUsage>,
  pricing: Partial<Record<HleModelId, HleModelPrice>> = HLE_DEFAULT_PRICING,
): HleCostEstimate {
  let usd = 0;
  const unpriced: HleModelId[] = [];
  for (const m of HLE_MODELS) {
    const u = usage[m];
    if (!u || (u.tokensIn === 0 && u.tokensOut === 0)) continue;
    const p = pricing[m];
    if (!p) { unpriced.push(m); continue; }
    usd += (u.tokensIn / 1_000_000) * p.in + (u.tokensOut / 1_000_000) * p.out;
  }
  return { usd: Math.round(usd * 10_000) / 10_000, unpriced };
}
